import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { mlmAPI } from '../../services/api';
import { Trophy, TrendingUp, Users, Activity, ChevronRight } from 'lucide-react';

const RankProgression = () => {
  const { isDarkMode } = useTheme();
  const [stats, setStats] = useState(null);
  const [rankHistory, setRankHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      mlmAPI.getNetworkStats(),
      mlmAPI.getRankHistory(),
    ])
      .then(([statsRes, historyRes]) => {
        setStats(statsRes.data);
        setRankHistory(historyRes.data?.history || historyRes.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const cardClass = `${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`;

  const nextRank = stats?.next_rank;
  const currentRank = stats?.current_rank || '—';
  const progress = stats?.rank_progress || (nextRank ? Math.min(
    ((stats?.weak_leg_vu || 0) / ((nextRank.needed_weak_leg_vu || nextRank.min_weak_leg_vu || 1))) * 100, 100
  ) : 0);

  const requirements = nextRank ? [
    { label: 'Weak Leg VU Needed', value: nextRank.needed_weak_leg_vu || nextRank.min_weak_leg_vu || 0, current: stats?.weak_leg_vu || 0 },
    { label: 'Direct Referrals Needed', value: nextRank.needed_direct_referrals || nextRank.min_direct_referrals || 0, current: stats?.total_direct || stats?.direct_referrals || 0 },
  ] : [];

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div>
        <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Rank Progression</h1>
        <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Your rank and achievements</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={cardClass}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Current Rank</h3>
            <div className="p-3 rounded-2xl bg-gradient-to-r from-orange-500 to-red-500 text-white">
              <Trophy className="w-6 h-6" />
            </div>
          </div>
          <p className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{currentRank}</p>
        </div>

        {nextRank && (
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Next Rank</h3>
              <div className="p-3 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                <TrendingUp className="w-6 h-6" />
              </div>
            </div>
            <p className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{nextRank.name}</p>
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-1">
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Progress</span>
                <span className="font-medium">{progress.toFixed(1)}%</span>
              </div>
              <div className={`w-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2.5`}>
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 h-2.5 rounded-full transition-all duration-500" style={{ width: `${Math.min(progress, 100)}%` }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {requirements.length > 0 && (
        <div className={cardClass}>
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Requirements for {nextRank?.name}</h2>
          <div className="space-y-4">
            {requirements.map((req) => {
              const pct = req.value > 0 ? Math.min((req.current / req.value) * 100, 100) : 0;
              return (
                <div key={req.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{req.label}</span>
                    <span className="font-medium">{req.current} / {req.value}</span>
                  </div>
                  <div className={`w-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2`}>
                    <div className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {rankHistory.length > 0 && (
        <div className={cardClass}>
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Rank History</h2>
          <div className="space-y-3">
            {rankHistory.map((entry, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="flex flex-col items-center">
                  <div className={`w-3 h-3 rounded-full bg-gradient-to-r from-orange-500 to-orange-600`} />
                  {i < rankHistory.length - 1 && <div className={`w-0.5 h-8 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />}
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{entry.rank_name || entry.rank}</p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {entry.achieved_date ? new Date(entry.achieved_date).toLocaleDateString() : ''}
                  </p>
                </div>
                {entry.bonus_earned > 0 && (
                  <span className="text-sm font-medium text-green-500">+${entry.bonus_earned?.toFixed(2)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default RankProgression;
